import { useState } from 'react';
import { Check, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { formatMoney } from '@/lib/format';
import { useServicePriceChangeRequests, type ServicePriceChangeRequest } from '@/api/services';
import { useApplyPriceChangeRequest, useRejectPriceChangeRequest } from '@/api/services-mutations';
import { DateInput } from '@/components/date-input';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Price-change banner — surfaces pending price-change requests for the service
 * (legacy `PriceChangeAlert`) and lets the user apply (from a chosen effective
 * date) or reject each one. Renders nothing when there are none.
 */
export function ServicePriceChangeAlert({ id, currency = 'VND' }: { id: number; currency?: string }) {
  const { data } = useServicePriceChangeRequests(id);
  const [applyReq, setApplyReq] = useState<ServicePriceChangeRequest | null>(null);
  const [rejectReq, setRejectReq] = useState<ServicePriceChangeRequest | null>(null);

  const pending = (data?.results ?? []).filter((r) => r.status === 'pending');
  if (pending.length === 0) return null;

  return (
    <>
      <Alert tone="warning" title={`${pending.length} pending price change${pending.length > 1 ? 's' : ''}`}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {pending.slice(0, 6).map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)' }}>
              <span>
                {r.price_type === 'recurring' ? 'Recurring' : 'Register'}:{' '}
                <span style={{ textDecoration: 'line-through', color: 'var(--vn-fg-muted)' }}>{formatMoney(r.old_price, currency)}</span>{' → '}
                <strong>{formatMoney(r.new_price, currency)}</strong>
                {r.effective_from ? <span style={{ color: 'var(--vn-fg-muted)' }}> · from {r.effective_from}</span> : null}
              </span>
              <span style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
                <Button variant="ghost" size="sm" onClick={() => setApplyReq(r)}><Check size={14} /> Apply</Button>
                <Button variant="ghost" size="sm" onClick={() => setRejectReq(r)}><X size={14} /> Reject</Button>
              </span>
            </div>
          ))}
        </div>
      </Alert>
      <ApplyDialog id={id} req={applyReq} currency={currency} onClose={() => setApplyReq(null)} />
      <RejectDialog id={id} req={rejectReq} onClose={() => setRejectReq(null)} />
    </>
  );
}

function ApplyDialog({ id, req, currency = 'VND', onClose }: { id: number; req: ServicePriceChangeRequest | null; currency?: string; onClose: () => void }) {
  const toast = useToast();
  const apply = useApplyPriceChangeRequest(id);
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const open = !!req;

  const submit = () => {
    if (!req || !effectiveFrom) return;
    apply.mutate({ id: req.id, effective_from: effectiveFrom }, {
      onSuccess: () => { toast.success('Price change applied'); onClose(); },
      onError: () => toast.error('Could not apply price change'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !apply.isPending) { apply.reset(); setEffectiveFrom(today()); onClose(); } }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Apply price change</DialogTitle>
          {req ? (
            <DialogDescription>
              {req.price_type === 'recurring' ? 'Recurring' : 'Register'} price {formatMoney(req.old_price, currency)} → {formatMoney(req.new_price, currency)}.
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogBody>
          <Field label="Effective from" required>
            {(p) => <DateInput {...p} value={effectiveFrom} invalid={!effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={apply.isPending} onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={apply.isPending} disabled={!effectiveFrom} onClick={submit}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ id, req, onClose }: { id: number; req: ServicePriceChangeRequest | null; onClose: () => void }) {
  const toast = useToast();
  const reject = useRejectPriceChangeRequest(id);
  const [note, setNote] = useState('');
  const open = !!req;

  const submit = () => {
    if (!req) return;
    reject.mutate({ id: req.id, note: note.trim() || undefined }, {
      onSuccess: () => { toast.success('Price change rejected'); onClose(); },
      onError: () => toast.error('Could not reject price change'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !reject.isPending) { reject.reset(); setNote(''); onClose(); } }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Reject price change</DialogTitle>
          <DialogDescription>This pending price change will be discarded.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Field label="Note" description="Optional reason for rejecting.">
            {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={reject.isPending} onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={reject.isPending} onClick={submit}>Reject</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
